/*
 * <elexa-video-strip>: product-page video strip engine (D2 video-carousel-engine).
 * Markup: snippets/elexa-video-strip.liquid. Needs assets/elexa-video.js (Elexa.video).
 *
 * Attributes: autoplay (muted autoplay of clips on screen, no play buttons), sound (sound buttons),
 * single (tapping play on one clip pauses the others in this strip).
 * Item: li.ex-vstrip__item > img.ex-vstrip__thumb, template[data-ex-vstrip-video], [data-ex-vstrip-play],
 * [data-ex-vstrip-sound]. The <video> is created from the template the first time it is needed.
 * - Play: muted, looping, inline, no controls; the item is marked playing once it really plays.
 * - Pause: the video layer is hidden (not destroyed) and the thumbnail shows again; play resumes.
 * - Sound: each clip starts muted. A sound choice made before playback unmutes 500 ms after start.
 * - Autoplay: clips load within ~50 px of the viewport, play while half visible, pause off-screen;
 *   skipped on 2G / data saver (the play buttons then show instead).
 */
(() => {
  if (customElements.get('elexa-video-strip')) return;

  const helper = () => window.Elexa && window.Elexa.video;

  class ElexaVideoStrip extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.single = this.hasAttribute('single');
      this.auto = this.hasAttribute('autoplay') && !(helper() && helper().slow());
      if (this.hasAttribute('autoplay') && !this.auto) this.removeAttribute('autoplay');
      this.items().forEach((item) => this.bindItem(item));
      this.addEventListener('shopify:block:select', (e) => {
        const item = e.target.closest && e.target.closest('.ex-vstrip__item');
        const list = this.querySelector('.ex-vstrip__list');
        if (item && list) list.scrollTo({ left: item.offsetLeft - list.offsetLeft, behavior: 'smooth' });
      });
    }

    disconnectedCallback() {
      (this.stops || []).forEach((stop) => stop());
      this.stops = [];
    }

    items() {
      return Array.from(this.querySelectorAll('.ex-vstrip__item'));
    }

    videoFor(item) {
      let video = item.querySelector('video');
      if (video) return video;
      const tpl = item.querySelector('template[data-ex-vstrip-video]');
      if (!tpl) return null;
      item.insertBefore(tpl.content.cloneNode(true), tpl);
      video = item.querySelector('video');
      if (!video) return null;
      video.muted = true;
      video.addEventListener('playing', () => this.mark(item, true));
      video.addEventListener('pause', () => this.mark(item, false));
      return video;
    }

    bindItem(item) {
      const play = item.querySelector('[data-ex-vstrip-play]');
      const sound = item.querySelector('[data-ex-vstrip-sound]');
      if (play) play.addEventListener('click', (e) => (e.preventDefault(), this.toggle(item)));
      if (sound) sound.addEventListener('click', (e) => (e.preventDefault(), this.toggleSound(item)));
      if (this.auto && helper()) {
        this.stops = this.stops || [];
        this.stops.push(
          helper().observe(item, {
            near: () => helper().load(this.videoFor(item)),
            enter: () => this.play(item, true),
            leave: () => this.pause(item),
          })
        );
      }
    }

    mark(item, playing) {
      item.classList.toggle('is-playing', playing);
      item.classList.toggle('is-paused', !playing);
      const btn = item.querySelector('[data-ex-vstrip-play]');
      if (btn) btn.setAttribute('aria-label', playing ? btn.dataset.labelPause : btn.dataset.labelPlay);
    }

    async play(item, auto = false) {
      const video = this.videoFor(item);
      if (!video || !helper()) return;
      // "One at a time" applies to taps; autoplay plays every clip on screen
      if (this.single && !auto) this.items().forEach((other) => other !== item && this.pause(other));
      video.muted = true;
      const ok = await helper().play(video, { muted: true });
      if (ok && item.dataset.sound === 'on') {
        clearTimeout(item.exSoundTimer);
        item.exSoundTimer = setTimeout(() => {
          video.muted = false;
        }, 500);
      }
    }

    pause(item) {
      const video = item.querySelector('video');
      if (video && !video.paused) video.pause();
    }

    toggle(item) {
      const video = item.querySelector('video');
      if (video && !video.paused) this.pause(item);
      else this.play(item);
    }

    toggleSound(item) {
      const on = item.dataset.sound !== 'on';
      item.dataset.sound = on ? 'on' : 'off';
      const btn = item.querySelector('[data-ex-vstrip-sound]');
      if (btn) {
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.setAttribute('aria-label', on ? btn.dataset.labelOff : btn.dataset.labelOn);
      }
      const video = item.querySelector('video');
      if (video && !video.paused) {
        clearTimeout(item.exSoundTimer);
        video.muted = !on;
      }
    }
  }

  customElements.define('elexa-video-strip', ElexaVideoStrip);
})();
